import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Layout, 
  Save, 
  Edit3, 
  RotateCcw, 
  Cloud, 
  Zap,
  PlusCircle,
  Shield,
  Users,
  Layers
} from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface DashboardToolbarProps {
  isEditMode: boolean;
  onToggleEdit: () => void;
  onResetLayout: () => void;
  onSaveLayout: () => void;
  onAddWidgetClick: () => void; 
  lastSynced?: Date;
  hasUnsavedChanges: boolean;
  
  // 🛡️ Hybrid Scope Props
  currentHouseholdId: string | null;
  activeScope: 'PERSONAL' | 'SHARED' | 'BOTH';
  onScopeChange: (scope: 'PERSONAL' | 'SHARED' | 'BOTH') => void;
}

/**
 * 🛠️ DashboardToolbar
 * Refactored for Hybrid Scoping: The control nexus for the personalized grid.
 * Displays the current data stream context to maintain privacy transparency.
 */
export const DashboardToolbar: React.FC<DashboardToolbarProps> = ({
  isEditMode,
  onToggleEdit,
  onResetLayout,
  onSaveLayout,
  onAddWidgetClick,
  lastSynced,
  hasUnsavedChanges,
  currentHouseholdId,
  activeScope,
  onScopeChange
}) => {
  const { t } = useTranslation();

  return (
    <div className="w-full h-20 bg-white/90 dark:bg-slate-950/90 backdrop-blur-xl border-b border-slate-100 dark:border-slate-800 flex items-center justify-between px-8 z-20 shadow-sm transition-colors duration-500">
      
      {/* --- LEFT SLOT: BRAND, STATUS & SCOPE --- */}
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-500/20">
            <Layout className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic leading-none">
              {t('dash.title', 'Command Center')}
            </h1>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="flex h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
                {t('dash.live_sync', 'Neural Sync Active')}
              </p>
            </div>
          </div>
        </div>

        <div className="hidden lg:block h-8 w-px bg-slate-100 dark:bg-slate-800 mx-2" />

        {/* 🛡️ PRIVACY SCOPE BADGE: Critical for Google Verification */}
        <div className="hidden md:flex items-center gap-3 px-4 py-2 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800">
          {activeScope === 'PERSONAL' || !currentHouseholdId ? (
            <Shield className="w-3.5 h-3.5 text-emerald-500" />
          ) : activeScope === 'SHARED' ? (
            <Users className="w-3.5 h-3.5 text-indigo-500" />
          ) : (
            <Layers className="w-3.5 h-3.5 text-violet-500" />
          )}
          <div className="flex flex-col">
            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none">Data Stream</span>
            <span className="text-[10px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-tighter">
              {currentHouseholdId ? activeScope : 'Personal Partition'}
            </span>
          </div>
        </div>
      </div>

      {/* --- RIGHT SLOT: MODES & CONTROLS --- */}
      <div className="flex items-center gap-3">
        {/* Sync Telemetry (Moved to right for balanced UI) */}
        <div className="hidden xl:flex items-center gap-2 mr-4 text-slate-400">
          <Cloud className="w-3.5 h-3.5 text-indigo-500/50" />
          <span className="text-[9px] font-black uppercase tracking-widest tabular-nums">
            {lastSynced 
              ? lastSynced.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
              : '---'}
          </span>
        </div>

        {/* Unsaved Changes Indicator */}
        {hasUnsavedChanges && isEditMode && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-100 dark:border-amber-800 animate-in fade-in slide-in-from-right-4">
            <Zap className="w-3 h-3 text-amber-500 animate-pulse" />
            <span className="text-[9px] font-black text-amber-600 uppercase tracking-tighter">
              {t('dash.unsaved', 'Unsaved Changes')}
            </span>
          </div>
        )}

        <div className="flex items-center bg-slate-100 dark:bg-slate-900/50 p-1 rounded-2xl border border-slate-200 dark:border-slate-800">
          {isEditMode ? (
            <div className="flex items-center gap-1">
              <button 
                onClick={onResetLayout}
                className="p-2.5 hover:bg-white dark:hover:bg-slate-800 rounded-xl text-slate-400 hover:text-rose-500 transition-all group"
                title={t('dash.reset', 'Reset to Default')}
              >
                <RotateCcw className="w-5 h-5 group-active:rotate-[-180deg] transition-transform duration-500" />
              </button>
              
              <Button 
                onClick={onSaveLayout}
                className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-xl shadow-lg shadow-indigo-500/20 hover:bg-indigo-700 transition-all"
              >
                <Save className="w-4 h-4" />
                <span className="text-[10px] font-black uppercase tracking-widest">{t('dash.save', 'Save Layout')}</span>
              </Button>
            </div>
          ) : (
            <Button
              onClick={onToggleEdit}
              className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl shadow-sm hover:shadow-md transition-all border border-slate-100 dark:border-slate-700"
            >
              <Edit3 className="w-4 h-4 text-indigo-500" />
              <span className="text-[10px] font-black uppercase tracking-widest">{t('dash.edit', 'Edit Grid')}</span>
            </Button>
          )}
        </div>

        {/* Sidebar Trigger (Only in Edit Mode) */}
        {isEditMode && (
          <button
            onClick={onAddWidgetClick}
            className="p-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl hover:scale-105 active:scale-95 transition-all shadow-xl"
            title={t('dash.add_widget', 'Add Widgets')}
          >
            <PlusCircle className="w-6 h-6" />
          </button>
        )}
      </div>
    </div>
  );
};